import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { UserPlus, Mail, Lock, User, Phone, FileText, Loader2 } from 'lucide-react';
import { clientAuthService, RegisterClientDto } from '@/services/client-auth.service';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, useToast, PhoneInput } from '@caruso/ui';
import 'react-phone-number-input/style.css';

export function ClientRegister() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState<RegisterClientDto>({
    name: '',
    email: '',
    password: '',
    phone: '',
    document: '',
  });

  const registerMutation = useMutation({
    mutationFn: (data: RegisterClientDto) => clientAuthService.register(data),
    onSuccess: (data) => {
      toast({
        title: 'Cadastro realizado com sucesso!',
        description: `Bem-vindo, ${data.client.name}!`,
        variant: 'success',
      });
      navigate('/client/dashboard');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao realizar cadastro',
        description: error.response?.data?.message || 'Tente novamente mais tarde',
        variant: 'destructive',
      });
    },
  });

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .slice(0, 14);
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData({ ...formData, phone: value || '' });
  };

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setFormData({ ...formData, document: formatted });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validações básicas
    if (!formData.name || !formData.email || !formData.password) {
      toast({
        title: 'Campos obrigatórios',
        description: 'Preencha nome, email e senha',
        variant: 'destructive',
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: 'Senha muito curta',
        description: 'A senha deve ter no mínimo 6 caracteres',
        variant: 'destructive',
      });
      return;
    }

    // Remove campos vazios opcionais e remove formatação
    const dataToSend: RegisterClientDto = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
    };

    if (formData.phone) dataToSend.phone = formData.phone; // Já vem em formato E.164 do PhoneInput
    if (formData.document) dataToSend.document = formData.document.replace(/\D/g, '');

    registerMutation.mutate(dataToSend);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="bg-neutral-900/50 backdrop-blur border-neutral-800">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-brand-gold/10 rounded-full flex items-center justify-center">
                <UserPlus className="w-8 h-8 text-brand-gold" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-white">Criar Conta</CardTitle>
            <CardDescription className="text-neutral-400">
              Cadastre-se para acompanhar seus pedidos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">
                  Nome Completo *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    type="text"
                    placeholder="Seu nome completo"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                    disabled={registerMutation.isPending}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    type="email"
                    placeholder="seu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                    disabled={registerMutation.isPending}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">
                  Senha *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    type="password"
                    placeholder="Mínimo 6 caracteres"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                    disabled={registerMutation.isPending}
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">
                  Telefone
                </label>
                <PhoneInput
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  defaultCountry="BR"
                  placeholder="Digite o telefone"
                  disabled={registerMutation.isPending}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-300">
                  CPF
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    type="text"
                    placeholder="000.000.000-00"
                    value={formData.document}
                    onChange={handleDocumentChange}
                    className="pl-10 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
                    disabled={registerMutation.isPending}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-brand-gold hover:bg-brand-gold/90 text-black font-semibold"
                disabled={registerMutation.isPending}
              >
                {registerMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cadastrando...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Criar Conta
                  </>
                )}
              </Button>

              <div className="text-center text-sm text-neutral-400 mt-4">
                Já tem uma conta?{' '}
                <Link
                  to="/client/login"
                  className="text-brand-gold hover:text-brand-gold/80 font-medium"
                >
                  Fazer login
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-sm text-neutral-500 hover:text-neutral-400"
          >
            Voltar para o site
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
